import { useState, useEffect, useCallback, useRef } from 'react';
import { pollOptimizationProgress } from '../utils/optimizerApi';

export interface OptimizationPollingState {
  isPolling: boolean;
  progress: {
    percentage: number;
    currentStep: string;
  };
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'ERROR' | 'TIMEOUT';
  error?: string;
  csvData?: string;
}

export interface UseOptimizationPollingReturn {
  state: OptimizationPollingState;
  startPolling: (taskId: string) => void;
  stopPolling: () => void;
  resetState: () => void;
}

const POLLING_INTERVAL = 5000; // 5 seconds
const TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes
const SIMULATED_PROGRESS_DURATION = 10000; // 10 seconds for progress simulation
const PROGRESS_UPDATE_INTERVAL = 200; // Update progress every 200ms for smooth animation

export const useOptimizationPolling = (): UseOptimizationPollingReturn => {
  const [state, setState] = useState<OptimizationPollingState>({
    isPolling: false,
    progress: {
      percentage: 0,
      currentStep: 'Aguardando início...'
    },
    status: 'PENDING'
  });

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const taskIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    console.log('🛑 Stopping optimization polling');
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    setState(prev => ({ ...prev, isPolling: false }));
    taskIdRef.current = null;
  }, []);

  const pollOnce = useCallback(async (taskId: string) => {
    try {
      console.log('🔄 Polling optimization progress...');
      const result = await pollOptimizationProgress(taskId);

      if (result.status === 'PENDING' || result.status === 'RUNNING') {
        setState(prev => ({
          ...prev,
          status: result.status,
          progress: {
            // Use simulated progress if backend doesn't provide percentage
            percentage: result.progress?.percentage || prev.progress.percentage,
            currentStep: result.progress?.current_step || prev.progress.currentStep
          }
        }));
      } else if (result.status === 'COMPLETED') {
        console.log('✅ Optimization completed successfully');
        stopPolling();
        setState(prev => ({
          ...prev,
          status: 'COMPLETED',
          progress: {
            percentage: 100,
            currentStep: 'Otimização concluída!'
          },
          csvData: (result as any).csvData
        }));
      } else if (result.status === 'ERROR') {
        console.error('❌ Optimization failed:', result.error?.message);
        stopPolling();
        setState(prev => ({
          ...prev,
          status: 'ERROR',
          error: result.error?.message || 'Erro desconhecido na otimização'
        }));
      }
    } catch (error) {
      console.error('❌ Error polling optimization:', error);
      stopPolling();
      setState(prev => ({
        ...prev,
        status: 'ERROR',
        error: error instanceof Error ? error.message : 'Erro de conexão durante o polling'
      }));
    }
  }, [stopPolling]);

  // Simulate progress over 10 seconds since backend doesn't provide progress
  const startSimulatedProgress = useCallback(() => {
    startTimeRef.current = Date.now();
    
    progressIntervalRef.current = setInterval(() => {
      if (!startTimeRef.current) return;
      
      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min((elapsed / SIMULATED_PROGRESS_DURATION) * 100, 95); // Cap at 95% until completion
      
      setState(prev => {
        if (prev.status === 'COMPLETED' || prev.status === 'ERROR') {
          return prev; // Don't update if already finished
        }
        
        let currentStep = 'Processando otimização...';
        if (progress < 25) {
          currentStep = 'Analisando dados...';
        } else if (progress < 50) {
          currentStep = 'Calculando rotas...';
        } else if (progress < 75) {
          currentStep = 'Otimizando cronograma...';
        } else {
          currentStep = 'Finalizando processamento...';
        }
        
        return {
          ...prev,
          progress: {
            percentage: Math.round(progress),
            currentStep
          }
        };
      });
    }, PROGRESS_UPDATE_INTERVAL);
  }, []);

  const startPolling = useCallback((taskId: string) => {
    console.log('🚀 Starting optimization polling for task:', taskId);
    
    // Stop any existing polling
    stopPolling();
    
    taskIdRef.current = taskId;
    
    setState({
      isPolling: true,
      progress: {
        percentage: 0,
        currentStep: 'Iniciando otimização...'
      },
      status: 'PENDING'
    });

    // Start simulated progress animation
    startSimulatedProgress();

    // Start polling immediately
    pollOnce(taskId);

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      if (taskIdRef.current) {
        pollOnce(taskIdRef.current);
      }
    }, POLLING_INTERVAL);

    // Set up timeout
    timeoutRef.current = setTimeout(() => {
      console.warn('⏰ Optimization polling timeout reached');
      stopPolling();
      setState(prev => ({
        ...prev,
        status: 'TIMEOUT',
        error: 'Tempo de otimização excedido (30 minutos)'
      }));
    }, TIMEOUT_DURATION);

  }, [pollOnce, stopPolling, startSimulatedProgress]);

  const resetState = useCallback(() => {
    stopPolling();
    setState({
      isPolling: false,
      progress: {
        percentage: 0,
        currentStep: 'Aguardando início...'
      },
      status: 'PENDING'
    });
  }, [stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    state,
    startPolling,
    stopPolling,
    resetState
  };
};